/**
 * Module dependencies.
 */

import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { EndpointKey, EndpointRequestOptions, resolveAppUrl } from 'app/core/utils/url-resolver';
import { AxiosInstance } from 'axios';
import { useInfiniteQuery } from '@tanstack/react-query';
import { castArray } from 'lodash';
import { useCallback, useMemo } from 'react';

/**
 * `Options` type.
 */

type Options = {
  endpoint: EndpointKey | null;
  options: EndpointRequestOptions | undefined;
  key: string | string[];
  pageSize?: number;
};

/**
 * `PaginatedResponse` type.
 */

type PaginatedResponse<T> = {
  results: T[];
  current: number;
  pages: number;
  total: number;
};

/**
 * Paginated CRUD Request.
 */

function paginatedCrudRequest<T>(
  client: AxiosInstance,
  endpoint: EndpointKey,
  options: any,
  page: number
): Promise<PaginatedResponse<T>> {
  const filters: any = {};

  if (options?.search && options.search.trim() !== '') {
    filters.search = options.search;
  }

  const requestOptions = {
    ...options,
    params: {
      ...options?.params,
      page: String(page),
      pageSize: String(options?.pageSize || 50),
      ...(Object.keys(filters).length > 0 ? { filters } : {})
    }
  };

  return client.get(resolveAppUrl(endpoint, requestOptions)).then(({ data }) => data?.data);
}

/**
 * Export `usePaginatedCrudRequest`.
 */

export function usePaginatedCrudRequest<T>({ endpoint, options, key, pageSize = 50 }: Options) {
  const client = useRequestProvider();

  const queryResult = useInfiniteQuery(['paginated-crud-request', ...castArray(key), JSON.stringify(options)], {
    queryFn: async ({ pageParam = 1 }) => {
      if (!endpoint) {
        return { results: [], current: 1, pages: 1, total: 0 };
      }

      return await paginatedCrudRequest<T>(client, endpoint, { ...options, pageSize }, pageParam);
    },
    getNextPageParam: (lastPage: PaginatedResponse<T>) => {
      if (lastPage.current < lastPage.pages) {
        return lastPage.current + 1;
      }

      return undefined;
    }
  });

  // Flatten all pages into a single array
  const data = useMemo(() => {
    if (!queryResult.data) {
      return [];
    }

    return queryResult.data.pages.flatMap(page => page.results);
  }, [queryResult.data]);

  // Check if there are more pages to load
  const hasNextPage = queryResult.hasNextPage;

  // Function to load more data
  const loadMore = useCallback(() => {
    if (hasNextPage && !queryResult.isFetchingNextPage) {
      queryResult.fetchNextPage();
    }
  }, [hasNextPage, queryResult]);

  return {
    data,
    isLoading: queryResult.isLoading,
    isFetchingNextPage: queryResult.isFetchingNextPage,
    hasNextPage,
    loadMore,
    error: queryResult.error
  };
}
